import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileSignature, Plus, ChevronRight, Clock, CheckCircle2, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const CONTRACT_STATUS = {
  draft:  { tone: "neutral", label: "Draft",  icon: Clock },
  sent:   { tone: "cyan",    label: "Sent",   icon: Send },
  viewed: { tone: "amber",   label: "Viewed", icon: Eye },
  signed: { tone: "emerald", label: "Signed", icon: CheckCircle2 },
  void:   { tone: "neutral", label: "Void",   icon: Clock },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

export default function ContractsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  useDocumentTitle("OmniDraftLab · Contracts");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [newPlayerId, setNewPlayerId] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts");
      setContracts(data);
    } catch {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/players").then(({ data }) => setPlayers(data)).catch(() => {});
  }, [isAdmin]);

  const openDetail = (c) => navigate(`/contracts/${c.id}`);

  const createContract = async () => {
    if (!newPlayerId) { toast.error("Select an athlete first"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/contracts", { player_id: newPlayerId });
      toast.success("Contract created — fill in the details");
      setOpen(false);
      setNewPlayerId("");
      navigate(`/contracts/${data.id}`);
    } catch {
      toast.error("Failed to create contract");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="contracts-page">
      <PageHeader
        title="Contracts"
        subtitle={isAdmin ? "Author advisory agreements and forward them to each family's hub." : "Your advisory agreement — review, download, and sign."}
        testId="contracts-header"
        actions={isAdmin && (
          <Button onClick={() => setOpen(true)} data-testid="contract-new-btn" className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200">
            <Plus className="h-4 w-4 mr-1.5" /> New contract
          </Button>
        )}
      />

      {loading ? (
        <LoadingState label="Loading contracts…" />
      ) : contracts.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          title={isAdmin ? "No contracts yet" : "No agreement shared yet"}
          description={isAdmin ? "Create a contract for a family and send it to their hub to review and sign." : "When your advisor sends you an agreement, it will appear here to review and sign."}
          testId="contracts-empty"
          action={isAdmin && (
            <Button onClick={() => setOpen(true)} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200">
              <Plus className="h-4 w-4 mr-1.5" /> New contract
            </Button>
          )}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contracts.map((c, i) => {
            const st = CONTRACT_STATUS[c.status] || CONTRACT_STATUS.draft;
            const Icon = st.icon;
            return (
              <motion.button
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                onClick={() => openDetail(c)}
                data-testid={`contract-card-${c.id}`}
                className="glass glass-hover text-left p-5 rounded-2xl group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-3">{c.player_position || "Athlete"} · {c.player_name || c.form?.athleteName}</div>
                    <div className="text-1 font-semibold text-lg mt-1 truncate">{c.title}</div>
                    <div className="text-2 text-sm mt-1 truncate">{c.form?.parent1Name ? `Family: ${c.form.parent1Name}` : "Family details pending"}</div>
                  </div>
                  <StatusPill tone={st.tone}><Icon className="h-3 w-3" /> {st.label}</StatusPill>
                </div>
                <div className="mt-4 flex items-center justify-between text-3 text-xs">
                  <span>Updated {fmt(c.updated_at)}</span>
                  <span className="inline-flex items-center gap-1 text-cyan-300 group-hover:gap-2 transition-all">{isAdmin ? "Open editor" : "Open"} <ChevronRight className="h-3.5 w-3.5" /></span>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* New contract dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="contract-new-dialog">
          <DialogHeader>
            <DialogTitle>New contract</DialogTitle>
            <DialogDescription>Select the athlete this advisory agreement is for. We'll prefill the athlete and family details from their profile.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-2 text-xs uppercase tracking-wider">Athlete</Label>
            <Select value={newPlayerId} onValueChange={setNewPlayerId}>
              <SelectTrigger className="mt-2" data-testid="contract-player-select">
                <SelectValue placeholder="Select an athlete…" />
              </SelectTrigger>
              <SelectContent>
                {players.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`contract-player-option-${p.id}`}>
                    {p.full_name} · {p.position}{p.school ? ` · ${p.school}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createContract} disabled={creating || !newPlayerId} data-testid="contract-create-confirm" className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200">
              {creating ? "Creating…" : "Create & edit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
